import type { FieldError, SubmitHandler } from 'react-hook-form'
import { useEffect, useRef, useState } from 'react'
import { useAddressContext } from 'src/components/checkout/steps/shipping/context/address-context'
import {
  makeCheckoutShippingEvent,
  sendEvent,
} from 'src/utils/restructure/analytics'
import { InputMask } from 'src/components/common/Input'
import { unmaskCEP } from 'src/utils/formatCEP'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude/dispatchAmplitudeEvent'
import { dispatchCepEnteredAndConfirmedEvent } from 'src/utils/amplitude/cepEvents'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import { SIMPLIFIED_SHIPPING_EXPERIMENT_ID } from 'src/components/common/StatsigExposure/StatsigExposure'
import { Statsig } from 'src/sdk/statsig'

import BtnModalClube from '../../product/sections/BtnInfoModalClube/BtnModalClube'
import { useProductPageContext } from '../../product/contexts'

import './styles.scss'

export function FreightForm() {
  const isSimplifiedShippingEnabled = Statsig.useStatsigFeatureGateValue(
    SIMPLIFIED_SHIPPING_EXPERIMENT_ID
  )

  const {
    product,
    currentSku,
    setFreightSimulationValue,
    freightSimulationValue,
    isLoadingFreightSimulation,
    simulationError,
  } = useProductPageContext()

  const { postalCode, setPostalCode, getCEP } = useAddressContext()
  const { pageData } = useGlobalContext()
  const standardObject = getStandardObject(pageData)

  const [error, setError] = useState<FieldError>()
  const formRef = useRef<HTMLFormElement>(null)
  const defaultFormValue = postalCode ?? 'Insira seu CEP'

  const isValidCEPSize = (cep: string | undefined) => {
    if (!cep) {
      return false
    }

    const unmaskedCep = unmaskCEP(cep)

    return unmaskedCep?.length === 8
  }

  const onSubmit: SubmitHandler<{ cep: string | undefined }> = async ({
    cep,
  }) => {
    if (!isValidCEPSize(cep)) {
      return
    }

    if ((!cep || cep === '') && !postalCode) {
      setError({ type: 'cep', message: 'CEP não encontrado' })

      return
    }

    const validCep = cep && cep !== '' ? cep : String(postalCode)

    try {
      setError(undefined)
      setPostalCode(validCep)
      await getCEP(validCep)

      await dispatchCepEnteredAndConfirmedEvent({
        pageData,
        cep: validCep,
        modalName: 'Simulacao de frete PDP',
        source: 'PDP',
      })

      const checkoutShippingEvent = makeCheckoutShippingEvent(
        product,
        currentSku
      )

      sendEvent(checkoutShippingEvent)
    } catch (err) {
      setError({ type: 'cep', message: 'CEP não encontrado' })
    }
  }

  const onBlur = ({ cep }: { cep: string }) => {
    if (isValidCEPSize(cep)) {
      return
    }

    setError({ type: 'cep', message: 'CEP não encontrado' })
    setPostalCode('')
    setFreightSimulationValue(null)
  }

  useEffect(() => {
    setError(undefined)
  }, [product])

  useEffect(() => {
    if (error && !simulationError) {
      return setError(undefined)
    }

    if (error) {
      return
    }

    if (postalCode && postalCode !== '' && simulationError) {
      setError({ type: 'cep', message: 'o frete será calculado no checkout' })

      dispatchAmplitudeEvent({
        eventName: 'Product Feature Viewed',
        eventData: {
          ...standardObject,
          type: 'simulação de frete',
          name: 'falha na api',
          'promotional element name': 'falha na api',
        },
      })
    }
  }, [
    postalCode,
    standardObject,
    isLoadingFreightSimulation,
    error,
    simulationError,
  ])

  return (
    // Keep the form element to allow for saved values and auto-fill
    <div className="w-full">
      <form
        onSubmit={(e) => {
          e.preventDefault()
        }}
        data-testid="form"
        className="w-full flex items-center flex-col restructure-tablet:flex-row"
        ref={formRef}
      >
        <div className="flex items-start rounded-round gap-2 w-full flex-1">
          <InputMask
            key={postalCode || 'empty'}
            name="cep"
            mask="99999-999"
            className="freigh-form-input h-[48px] w-full py-3 px-4 border border-solid border-lightGray text-base placeholder:text-neutral16 !text-restructure-primary outline-none rounded-none focus:rounded-none focus:border-primaryBlue focus:border-2"
            classNameLabel="flex-1 w-full md:w-auto bg-transparent"
            classNameError="border-redDiscount"
            placeholder={postalCode ?? 'Insira seu CEP'}
            id="inputCep"
            data-testid="pdp-freight-cep-input"
            errors={error}
            defaultValue={defaultFormValue}
            alwaysShowMask={false}
            inputMode="numeric"
            onChange={(e) => {
              onSubmit({ cep: e.target.value })
            }}
            onBlur={(e) => {
              onBlur({ cep: e.target.value })
            }}
          />
        </div>
      </form>

      {isSimplifiedShippingEnabled && !!freightSimulationValue && (
        <div className="text-xs text-tertiary font-medium mt-2">
          Valores e prazos podem mudar conforme os itens do carrinho.
        </div>
      )}

      <div className="mt-6 w-full">
        <BtnModalClube freight={freightSimulationValue} />
      </div>
    </div>
  )
}
